import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { ToastType, useToastStore } from "@/shared/stores/toastStore";
import { Icons } from "@/shared/icons";
import { ToastBadge, ToastBadgeTitle, ToastCloseButton } from "./styles";
import { MascotFeedback, MascotVariant } from "../../MascotFeedback";

/**
 * Contenedor global del Toast.
 *
 * Cualquier componente puede dispararlo con:
 *   const { showToast } = useToastStore();
 *   showToast({ type: "info", title: "Mi mensaje", duration: 2500 });
 */

const TOAST_VARIANT_MAP: Partial<Record<ToastType, MascotVariant>> = {
  success: "success",
  error: "error",
  warning: "warning",
};

export const Toast = () => {

  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  const activeToast = toasts[toasts.length - 1];

  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleClose = useCallback(() => {
    if (!activeToast) return;

    removeToast(activeToast.id);
    activeToast.onClose?.();
  }, [activeToast, removeToast]);

  useEffect(() => {
    if (!activeToast || activeToast.dismissible) return;

    const duration = activeToast.duration ?? 2500;
    const timer = setTimeout(() => {
      removeToast(activeToast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [activeToast, removeToast]);

  // Un toast que requiere cierre manual recibe el foco para que sea operable con teclado.
  // Escape se maneja en el propio botón: solo cierra el toast si el foco está en él,
  // así no compite con otros overlays (menú móvil, modales) que también usan Escape.
  useEffect(() => {
    if (!activeToast?.dismissible) return;

    closeButtonRef.current?.focus();
  }, [activeToast]);

  const handleCloseKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      handleClose();
    }
  };

  if (!activeToast) return null;

  const mascotVariant = TOAST_VARIANT_MAP[activeToast.type];

  return (
    <ToastBadge
      $hasIcon={Boolean(mascotVariant)}
      $dismissible={activeToast.dismissible}
      role={activeToast.type === "error" ? "alert" : "status"}
    >
      {mascotVariant && (
        <MascotFeedback
          variant={mascotVariant}
          name="cloud"
          decorative
          size="sm"
          dropShadow="glow"
        />
      )}
      <ToastBadgeTitle>{activeToast.title}</ToastBadgeTitle>
      {activeToast.dismissible && (
        <ToastCloseButton
          ref={closeButtonRef}
          type="button"
          aria-label="Cerrar notificación"
          onClick={handleClose}
          onKeyDown={handleCloseKeyDown}
        >
          <Icons name="iconClose" width={24} height={24} />
        </ToastCloseButton>
      )}
    </ToastBadge>
  );
};
